import { useEffect, useMemo, useState } from 'react';

const API_BASE = 'https://api.insightos.co.uk';

export default function useJobPolling(jobId, options = {}) {
  const {
    enabled = true,
    pollInterval = 2000,
    maxNetworkErrors = 10,
    statusUrl = null,
  } = options;

  const [job, setJob] = useState(null);
  const [status, setStatus] = useState(jobId ? 'waiting' : 'idle');
  const [error, setError] = useState(null);
  const [networkErrorCount, setNetworkErrorCount] = useState(0);

  useEffect(() => {
    if (!jobId || !enabled) {
      return undefined;
    }

    let cancelled = false;
    let intervalId;

    const pollJob = async () => {
      try {
        const resolvedStatusUrl = statusUrl
          ? (String(statusUrl).startsWith('http') ? String(statusUrl) : `${API_BASE}${statusUrl}`)
          : `${API_BASE}/api/redisjobs/${jobId}`;

        const response = await fetch(resolvedStatusUrl, {
          method: 'GET',
          credentials: 'include',
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch job status');
        }

        if (cancelled) {
          return;
        }

        setJob(data);
        setStatus(data.status || 'waiting');
        setError(null);
        setNetworkErrorCount(0);

        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(intervalId);
        }
      } catch (pollError) {
        if (cancelled) {
          return;
        }

        setNetworkErrorCount((currentCount) => {
          const nextCount = currentCount + 1;
          if (nextCount >= maxNetworkErrors) {
            setStatus('failed');
            setError('Unable to reach the job status service.');
            clearInterval(intervalId);
          }
          return nextCount;
        });
      }
    };

    pollJob();
    intervalId = window.setInterval(pollJob, pollInterval);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [enabled, jobId, maxNetworkErrors, pollInterval, statusUrl]);

  const progress = useMemo(() => {
    if (!job || !job.progress) {
      return 0;
    }

    if (typeof job.progress === 'number') {
      return job.progress;
    }

    return Number(job.progress.percent || 0);
  }, [job]);

  const stage = useMemo(() => {
    if (!job || !job.progress || typeof job.progress !== 'object') {
      return '';
    }

    return job.progress.stage || '';
  }, [job]);

  return {
    error,
    job,
    progress,
    stage,
    status,
    isComplete: status === 'completed',
    isFailed: status === 'failed',
    isRunning: status === 'active' || status === 'waiting' || status === 'delayed',
  };
}
